import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { Invitation, InvitationDraft, InvitationSummary, Money } from '@debtbot/shared';
import { ApiError, NetworkError } from '../api/errors';
import { useConnections, useCreateInvitation, useInvitations, useTrackEvent } from '../api/hooks';
import { Button } from '../components/Button';
import { ErrorBanner } from '../components/ErrorBanner';
import { FirstStep } from '../components/FirstStep';
import { InviteDraftScreen } from '../components/InviteDraftScreen';
import { InviteLinkSheet } from '../components/InviteLinkSheet';
import { MainButtonFallback } from '../components/MainButtonFallback';
import { PendingInvites } from '../components/PendingInvites';
import { PersonRow } from '../components/PersonRow';
import { ListSkeleton } from '../components/Skeleton';
import { useToast } from '../components/Toast';
import { useLocale, useT } from '../i18n';
import { type BalanceFilter, filterConnections, isBalanceFilter } from '../lib/connections';
import { formatMoney } from '../lib/format';
import { homeState, loadOnboardingSkipped, saveOnboardingSkipped } from '../lib/home-state';
import {
  loadCachedInvite,
  recallInvite,
  rememberInvite,
  reusableInvite,
  saveCachedInvite,
} from '../lib/invite-cache';
import { inviteLink, inviteShareText } from '../lib/invite-share';
import { haptic } from '../telegram/haptic';
import { shareInviteLink } from '../telegram/share';
import { useMainButton } from '../telegram/ui';

const SUMMARY_TONE = {
  owed: { text: 'text-positive', ring: 'ring-positive' },
  owe: { text: 'text-negative', ring: 'ring-negative' },
} as const;

/** Итог по направлению; заодно переключатель фильтра списка (повторный тап снимает). */
function SummaryCell({
  label,
  items,
  tone,
  active,
  onToggle,
}: {
  label: string;
  items: Money[];
  tone: BalanceFilter;
  active: boolean;
  onToggle: () => void;
}) {
  const money = items[0] ?? { amount: 0, currency: 'UZS' };
  const { text, ring } = SUMMARY_TONE[tone];
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      className={`flex-1 rounded-xl px-3 py-2 text-left transition active:opacity-70 ${
        active ? `bg-bg ring-2 ${ring}` : ''
      }`}
    >
      <div className="text-sm text-hint">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${text}`}>{formatMoney(money)}</div>
    </button>
  );
}

export function HomeScreen() {
  const t = useT();
  const locale = useLocale();
  const navigate = useNavigate();
  const connections = useConnections();
  const createInvite = useCreateInvitation();
  const trackEvent = useTrackEvent();
  const toast = useToast();
  // «Пригласить» → сначала короткая форма первой записи (или «Пропустить»).
  const [draftOpen, setDraftOpen] = useState(false);
  // Вне Telegram share-sheet недоступен — показываем ссылку в шторке.
  const [fallbackInvite, setFallbackInvite] = useState<Invitation | null>(null);
  const [skipped, setSkipped] = useState(loadOnboardingSkipped);
  const hasPeople = (connections.data?.items.length ?? 0) > 0;
  // Без связей главная зависит от отправленных ссылок — второй запрос только для таких
  const invitations = useInvitations(connections.data !== undefined && !hasPeople);
  const state =
    connections.data && (hasPeople || invitations.data)
      ? homeState({
          connections: connections.data.items.length,
          pendingInvites: invitations.data?.items.length ?? 0,
          skipped,
        })
      : null;

  // Фильтр и поиск — в URL, чтобы пережить переход в карточку человека и «Назад».
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get('f');
  const filter = isBalanceFilter(rawFilter) ? rawFilter : null;
  const query = params.get('q') ?? '';
  const updateParams = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  const toggleFilter = (f: BalanceFilter) => {
    haptic.select();
    updateParams({ f: filter === f ? null : f });
  };
  const visible = connections.data
    ? filterConnections(connections.data.items, { filter, query })
    : [];

  const invite = () => {
    haptic.tap();
    setDraftOpen(true);
  };

  const firstStepCreate = () => {
    trackEvent.mutate({ event: 'onboarding_cta', props: { action: 'create' } });
    invite();
  };
  const firstStepSkip = () => {
    haptic.select();
    trackEvent.mutate({ event: 'onboarding_cta', props: { action: 'skip' } });
    saveOnboardingSkipped();
    setSkipped(true);
  };

  const share = (inv: Invitation) => {
    const text = inviteShareText(t, locale, inv.draft);
    if (!shareInviteLink(inviteLink(inv), text)) setFallbackInvite(inv);
  };

  /**
   * Создать приглашение и сразу открыть системный шаринг. Без черновика — переиспользуем
   * последнюю ссылку; с черновиком всегда новая: у ссылки из кэша другая (или никакая) запись.
   */
  const send = async (draft: InvitationDraft | null) => {
    setDraftOpen(false);
    try {
      const count = connections.data?.items.length ?? 0;
      let inv = draft ? null : reusableInvite(loadCachedInvite(), count);
      if (!inv) {
        inv = await createInvite.mutateAsync({ source_channel: 'share', draft });
        if (!draft) saveCachedInvite(inv, count);
        rememberInvite(inv);
      }
      share(inv);
    } catch (err) {
      haptic.error();
      const limit = err instanceof ApiError && err.code === 'INVITATION_LIMIT_REACHED';
      const network = err instanceof NetworkError;
      toast({
        text: limit ? t.home.inviteLimit : network ? t.errors.network : t.errors.generic,
        duration: 4000,
      });
    }
  };

  /** Та же ссылка, если устройство её помнит; иначе новая (прежняя остаётся действительной). */
  const resend = (inv: InvitationSummary) => {
    haptic.tap();
    const known = recallInvite(inv.id);
    if (known) share(known);
    else void send(null);
  };

  const goAdd = () => {
    const items = connections.data?.items ?? [];
    void navigate(items.length === 1 ? `/c/${items[0]!.id}/new` : '/add');
  };
  // Пока открыта форма черновика, «Добавить» прячем: у оверлея свои кнопки
  const addVisible = hasPeople && !draftOpen;
  useMainButton({ text: t.home.add, visible: addVisible, onClick: goAdd });

  return (
    <main className="min-h-dvh bg-bg text-text px-4 py-4 pb-24 flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.home.title}</h1>
        <button
          type="button"
          aria-label={t.home.settings}
          className="rounded-full p-2 text-xl active:opacity-60"
          onClick={() => void navigate('/settings')}
        >
          ⚙
        </button>
      </header>

      {/* isLoading, не isPending: выключенный запрос приглашений тоже «pending» */}
      {(connections.isPending || invitations.isLoading) && <ListSkeleton />}
      {connections.isError && (
        <ErrorBanner error={connections.error} onRetry={() => void connections.refetch()} />
      )}
      {invitations.isError && (
        <ErrorBanner error={invitations.error} onRetry={() => void invitations.refetch()} />
      )}

      {state === 'first-step' && (
        <FirstStep
          pending={createInvite.isPending}
          onCreate={firstStepCreate}
          onSkip={firstStepSkip}
        />
      )}

      {state === 'pending' && invitations.data && (
        <PendingInvites
          items={invitations.data.items}
          hasLink={(id) => recallInvite(id) !== null}
          pending={createInvite.isPending}
          onResend={resend}
          onInvite={invite}
        />
      )}

      {state === 'empty' && (
        <section className="flex flex-col items-center gap-3 rounded-2xl bg-secondary-bg px-6 py-8 text-center">
          <div className="text-4xl">👋</div>
          <h2 className="text-lg font-semibold">{t.home.emptyTitle}</h2>
          <p className="text-hint">{t.home.emptyBody}</p>
          <Button onClick={invite} disabled={createInvite.isPending}>
            {t.home.invite}
          </Button>
        </section>
      )}

      {state === 'list' && connections.data && (
        <>
          <section className="flex gap-2 rounded-2xl bg-secondary-bg p-1.5">
            <SummaryCell
              label={t.home.owedToMe}
              items={connections.data.summary.owed_to_me}
              tone="owed"
              active={filter === 'owed'}
              onToggle={() => toggleFilter('owed')}
            />
            <SummaryCell
              label={t.home.iOwe}
              items={connections.data.summary.i_owe}
              tone="owe"
              active={filter === 'owe'}
              onToggle={() => toggleFilter('owe')}
            />
          </section>
          <input
            type="search"
            data-testid="search-input"
            value={query}
            onChange={(e) => updateParams({ q: e.target.value })}
            placeholder={t.home.search}
            className="rounded-xl bg-secondary-bg px-4 py-3 outline-none placeholder:text-hint"
          />
          {visible.length > 0 ? (
            <section className="divide-y divide-secondary-bg">
              {visible.map((c) => (
                <PersonRow key={c.id} c={c} onClick={() => void navigate(`/c/${c.id}`)} />
              ))}
            </section>
          ) : (
            <p className="py-6 text-center text-hint">{t.home.nothingFound}</p>
          )}
          <Button variant="secondary" onClick={invite} disabled={createInvite.isPending}>
            ⊕ {t.home.invite}
          </Button>
        </>
      )}
      <MainButtonFallback text={t.home.add} visible={addVisible} onClick={goAdd} />
      <InviteDraftScreen
        open={draftOpen}
        pending={createInvite.isPending}
        onSend={(draft) => void send(draft)}
        onSkip={() => void send(null)}
        onClose={() => setDraftOpen(false)}
      />
      <InviteLinkSheet
        invite={fallbackInvite}
        text={inviteShareText(t, locale, fallbackInvite?.draft ?? null)}
        onClose={() => setFallbackInvite(null)}
      />
    </main>
  );
}
